import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api, clearSession, hasSession, mintCrossing, redeemCrossing, setSession } from "../lib/api";
import { CROSS_PARAM, currentPortal, loadPortals } from "../lib/portals";

const WorldContext = createContext(null);

export const useWorld = () => useContext(WorldContext);

/**
 * If we arrived carrying a crossing token, redeem it BEFORE anything else and
 * strip it from the URL. This is the Seam doing real work: each Portal is a
 * different origin, so it cannot see another Portal's session — identity is
 * handed over explicitly, once, and the matching Pouch balance is the proof.
 */
const consumeCrossing = async (portal) => {
  const url = new URL(window.location.href);
  const token = url.searchParams.get(CROSS_PARAM);
  if (!token) return false;
  url.searchParams.delete(CROSS_PARAM);
  window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  try {
    const data = await redeemCrossing(token, portal);
    return data;
  } catch (e) {
    return false;
  }
};

export const WorldProvider = ({ children }) => {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);
  const [portals, setPortals] = useState([]);
  const [graduated, setGraduated] = useState(false);
  const portalRef = useRef("endeavorum");
  // Read synchronously at call time so a request fired immediately after the
  // toggle is never routed with the previous mode.
  const liveRef = useRef(false);
  const setLiveNow = useCallback((v) => {
    liveRef.current = v;
    setLive(v);
  }, []);
  const isLive = useCallback(() => liveRef.current, []);

  const refresh = useCallback(async () => {
    const list = await loadPortals().catch(() => []);
    setPortals(list);
    const portal = currentPortal(list);
    portalRef.current = portal;
    const crossed = await consumeCrossing(portal);
    if (crossed?.graduated) setGraduated(true);

    const boot = async (retry) => {
      if (!hasSession()) {
        // ALPHA / JUDGING WINDOW (founder's call, 31 Aug 2026): every Portal
        // now walks a visitor straight in, the same way endeavorum.com does.
        // The two `.app` domains exist to prove the Seam, and a sign-in gate in
        // front of that proof costs a tired judge one click and one moment of
        // "do I have to register?". The Threshold is still there on /enter for
        // anyone who wants to arrive as themselves and leave an address.
        //
        // A Nudger arrives at /card/:id with no session, and CardPage redeems
        // their invite itself. Conjuring a judge session here would run FIRST and
        // wipe the very PlayCard they were invited to look at.
        const onCard = window.location.pathname.startsWith("/card/");
        const onThreshold = window.location.pathname.startsWith("/enter");
        const onClaim = window.location.pathname.startsWith("/claim");
        if (onCard || onThreshold || onClaim) {
          setMe(null);
          return null;
        }
        try {
          const { data } = await api.post("/auth/judge");
          setSession(data.token);
        } catch (e) {
          setMe(null);
          return null;
        }
      }
      try {
        const { data } = await api.get("/me");
        setMe(data);
        return data;
      } catch (e) {
        // A token signed with a retired SESSION_SECRET — or simply an expired one
        // — is not a logged-out visitor, it is a WEDGED one. Dropping the token
        // and stopping left the Atlas showing a gate to anyone whose stored
        // session had gone stale, which is exactly what a secret rotation does to
        // every returning visitor at once. Drop it and boot again, once.
        clearSession();
        if (retry) return boot(false);
        setMe(null);
        return null;
      }
    };

    try {
      return await boot(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enter = useCallback(
    async (email, displayName) => {
      const { data } = await api.post("/auth/enter", {
        email,
        display_name: displayName,
        portal: portalRef.current,
      });
      setSession(data.token);
      return refresh();
    },
    [refresh]
  );

  const enterAsJudge = useCallback(async () => {
    const { data } = await api.post("/auth/judge");
    setSession(data.token);
    return refresh();
  }, [refresh]);

  const leave = useCallback(() => {
    clearSession();
    setMe(null);
  }, []);

  const mark = useCallback((realm, path) => {
    api.post("/engagement", { realm, last_state: path }).catch(() => {});
  }, []);

  /**
   * Cross into another Portal — any Portal to any Portal.
   *
   * Same origin (or no origin configured yet) -> an ordinary route change.
   * A different origin -> mint a signed single-use crossing token and hand
   * identity over in the URL. Callers never need to know which case they are in,
   * which is what stops the Seam from being reimplemented per Portal.
   */
  const cross = useCallback(
    async (portalKey, path) => {
      const target = portals.find((p) => p.key === portalKey);
      const fallback = path || target?.entry_path || "/";
      if (!target || !target.origin || target.origin === window.location.origin) {
        return { external: false, path: fallback };
      }
      const minted = await mintCrossing(portalKey, path);
      const url = new URL(minted.path || fallback, minted.origin || target.origin);
      url.searchParams.set(CROSS_PARAM, minted.crossing_token);
      window.location.assign(url.toString());
      return { external: true, path: fallback };
    },
    [portals]
  );

  return (
    <WorldContext.Provider
      value={{
        me,
        loading,
        refresh,
        enter,
        enterAsJudge,
        leave,
        mark,
        cross,
        portals,
        portal: portalRef.current,
        graduated,
        clearGraduated: () => setGraduated(false),
        live,
        setLive: setLiveNow,
        isLive,
      }}
    >
      {children}
    </WorldContext.Provider>
  );
};
